import {useTranslation} from 'react-i18next'
import {Skeleton} from '@/components/ui/skeleton'
import {FotoAnalysisChip} from '@/components/foto-analysis-chip'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  useEntrevistaFotos,
  useHallazgoFotoAnalysisAction,
  type Entrevista,
  type Hallazgo,
  type HallazgoFoto,
} from '@/hooks/sgsst/riesgos/use-entrevista'

type Turno = Entrevista['turnos'][number]
type QaPair = {pregunta: Turno; respuesta: Turno | null}

// Tiene que coincidir con CIERRE_TEXTO_LIBRE_PREGUNTA del backend (questionnaire_engine.py).
const CIERRE_TEXTO_LIBRE_PREGUNTA = 'Cuéntenos con sus palabras — escriba lo que le preocupa.'

function pairTurnos(turnos: Turno[]): QaPair[] {
  const pairs: QaPair[] = []
  for (let i = 0; i < turnos.length; i += 1) {
    const turno = turnos[i]
    if (turno.role !== 'asistente') continue
    const next = turnos[i + 1]
    pairs.push({pregunta: turno, respuesta: next && next.role === 'empleado' ? next : null})
  }
  return pairs
}

function HallazgoFotos({
  businessId,
  employeeId,
  entrevistaId,
  hallazgo,
  fotos,
  isLoading,
}: {
  businessId: string
  employeeId: string
  entrevistaId: string
  hallazgo: Hallazgo
  fotos: HallazgoFoto[] | undefined
  isLoading: boolean
}) {
  const {t} = useTranslation()
  const analysisAction = useHallazgoFotoAnalysisAction(businessId)
  // Las acciones del chip de IA son del operador (vision-fotos-spec §7); el modo dios de solo lectura las apaga.
  const editable = !useGodModeReadOnly()
  if (isLoading) return <Skeleton className="h-8 w-full" />
  if (!fotos || fotos.length === 0) return null
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="entrevista-hallazgo-fotos">
      {fotos.map((foto) => (
        <div key={foto.id} className="w-32 space-y-1" data-testid="entrevista-hallazgo-foto">
          <a
            href={foto.download_url}
            target="_blank"
            rel="noreferrer"
            className="block h-16 w-16 overflow-hidden rounded-md border border-border"
            title={foto.filename}>
            <img
              src={foto.download_url}
              alt={t('employees.transcript.fotoAlt')}
              className="h-full w-full object-cover"
            />
          </a>
          <FotoAnalysisChip
            foto={foto}
            hint={hallazgo.foto_hint ?? null}
            editable={editable}
            onDismiss={() =>
              analysisAction.mutate({
                employeeId,
                entrevistaId,
                hallazgoId: hallazgo.id,
                fotoId: foto.id,
                action: 'dismiss',
              })
            }
            onRetry={() =>
              analysisAction.mutate({
                employeeId,
                entrevistaId,
                hallazgoId: hallazgo.id,
                fotoId: foto.id,
                action: 'reanalyze',
              })
            }
          />
        </div>
      ))}
    </div>
  )
}

/** `employeeId` solo en las lecturas del operador: el historial propio no lee fotos aparte. */
export function EntrevistaTranscriptView({
  businessId,
  employeeId,
  entrevista,
  isLoading,
}: {
  businessId: string
  employeeId?: string
  entrevista: Entrevista | undefined
  isLoading: boolean
}) {
  const {t} = useTranslation()
  const pairs = entrevista ? pairTurnos(entrevista.turnos) : []
  const hallazgoByCita = new Map((entrevista?.hallazgos ?? []).map((h) => [h.cita, h]))
  // Una sola petición de fotos por entrevista, nunca una por hallazgo (PYTHON-1W).
  const {data: fotosByHallazgo, isLoading: fotosLoading} = useEntrevistaFotos(
    businessId,
    employeeId,
    entrevista?.id ?? undefined
  )

  if (isLoading || !entrevista) return <Skeleton className="h-40 w-full" />
  if (pairs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="entrevista-transcript-empty">
        {t('employees.transcript.empty')}
      </p>
    )
  }
  return (
    <>
      <div className="max-h-96 space-y-4 overflow-y-auto pr-1" data-testid="entrevista-transcript-thread">
        {pairs.map((pair, i) => {
          const hallazgo = pair.respuesta ? hallazgoByCita.get(pair.respuesta.texto) : undefined
          const esCierreTextoLibre = pair.pregunta.texto === CIERRE_TEXTO_LIBRE_PREGUNTA && pair.respuesta
          if (esCierreTextoLibre) {
            return (
              <div
                key={pair.pregunta.seq}
                className="notice-amber space-y-1 rounded-lg px-3.5 py-3 text-sm"
                data-testid="entrevista-transcript-cierre-libre">
                <p className="font-mono text-[10px] font-medium tracking-wider uppercase">
                  {t('employees.transcript.cierreLibreLabel')}
                </p>
                <p className="text-foreground">
                  {t('employees.transcript.cierreLibreAgregado', {texto: pair.respuesta?.texto})}
                </p>
              </div>
            )
          }
          return (
            <div key={pair.pregunta.seq} className="space-y-1" data-testid="entrevista-transcript-qa">
              <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('employees.transcript.questionLabel', {n: i + 1})}
              </p>
              <p className="text-sm text-foreground">{pair.pregunta.texto}</p>
              {pair.respuesta ? (
                <p className="rounded-lg bg-secondary px-3 py-2 text-sm text-foreground">{pair.respuesta.texto}</p>
              ) : (
                <p className="text-xs text-muted-foreground">{t('employees.transcript.pending')}</p>
              )}
              {hallazgo && employeeId && entrevista.id && (
                <HallazgoFotos
                  businessId={businessId}
                  employeeId={employeeId}
                  entrevistaId={entrevista.id}
                  hallazgo={hallazgo}
                  fotos={fotosByHallazgo?.[hallazgo.id]}
                  isLoading={fotosLoading}
                />
              )}
            </div>
          )
        })}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{t('employees.transcript.provenanceNote')}</p>
    </>
  )
}
